import React, { useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { getBankMedia } from '../../requests/documentDatas';
import { normalizeBankMedia } from '../../shared/media/mediaBank';
import { MEDIA_CONTEXTS, resolutionOptions } from '../../shared/media/mediaResolution';

/**
 * Palier de résolution d'un média posé sur une box.
 *
 * La box ne persiste que l'URL retenue et l'id du média : rouvrir ce sélecteur
 * demande de relire `variants`. On les relit par média (`media_bank/:media_id`)
 * plutôt que de recharger toute la banque pour un seul fichier.
 *
 * Cache module : la même image revient d'une box à l'autre, et le palier se
 * change souvent deux fois de suite avant de retenir le bon.
 */
const cache = new Map();

/* Les paliers sont calculés par un worker après l'upload : quelques secondes. */
const RETRY_DELAY_MS = 2000;
const MAX_ATTEMPTS = 6;

const MediaResolutionSelect = ({ mediaId, resolution, onChange }) => {
  const { t } = useTranslation();
  const currentDocument = useStore(({ currentDocument }) => currentDocument);
  const [media, setMedia] = useState(() => cache.get(String(mediaId)) ?? null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mediaId == null || !currentDocument?.id) return undefined;

    const cached = cache.get(String(mediaId));
    if (cached) {
      setMedia(cached);
      return undefined;
    }

    let cancelled = false;
    let timer;

    const load = (attempt) => {
      setLoading(true);
      getBankMedia({ ...currentDocument, mediaId })
        .then((raw) => {
          if (cancelled) return;
          const normalized = normalizeBankMedia(raw);
          setMedia(normalized);
          if (!normalized) return;
          // Juste après l'upload, le back n'a pas encore les dimensions : tous
          // les paliers retombent sur l'original. On ne garde pas ce média en
          // cache et on le relit jusqu'à ce que les paliers soient calculés.
          if (normalized.width != null) cache.set(String(mediaId), normalized);
          else if (attempt < MAX_ATTEMPTS) timer = setTimeout(() => load(attempt + 1), RETRY_DELAY_MS);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    load(1);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mediaId, currentDocument]);

  if (!media) return loading ? <Spin size="small" /> : null;

  const options = resolutionOptions(media, MEDIA_CONTEXTS.pdf);
  if (options.length < 2) return null;

  return (
    // data-outside-ignore + preventDefault : choisir une résolution ne
    // déselectionne pas la box (cf. useOutsideAlerter), sinon le panel se
    // referme au moment du clic.
    <div
      data-outside-ignore="true"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
    >
      <Select
        size="small"
        style={{ width: '100%' }}
        value={options.some((o) => o.value === resolution) ? resolution : options[0].value}
        options={options}
        onChange={(next) => onChange(next, media)}
        aria-label={t('resolution')}
        getPopupContainer={(trigger) => trigger.parentElement}
      />
    </div>
  );
};

export default MediaResolutionSelect;
